import { RefObject, useEffect } from "react";

/**
 * Publish the rendered height of a sticky "chrome" element as the
 * `--iq-ag-header-top` CSS variable on a host ancestor, so a sticky
 * BaseAgGrid column header (which reads that variable) pins flush below
 * the chrome instead of at the very top of the scroll container.
 *
 * Used by the Scenario Results page (Tabs nav + ribbon chrome) and the
 * project page (the sticky "Scenarios" row). One ResizeObserver on our
 * own element — no `querySelector` into Blueprint internals — re-measures
 * on font load / density change / resize.
 *
 * `hostRef` must be a common ancestor of both the chrome and the grid so
 * the variable is inherited by the AG Grid header.
 */
export const useChromeHeightVar = (
    hostRef: RefObject<HTMLDivElement | null>,
    chromeRef: RefObject<HTMLDivElement | null>,
    enabled: boolean
): void => {
    useEffect(() => {
        if (!enabled) return;
        const host = hostRef.current;
        const chrome = chromeRef.current;
        if (!host || !chrome) return;
        const update = () =>
            host.style.setProperty(
                "--iq-ag-header-top",
                `${chrome.offsetHeight}px`
            );
        update();
        const observer = new ResizeObserver(update);
        observer.observe(chrome);
        return () => observer.disconnect();
    }, [hostRef, chromeRef, enabled]);
};
